/* eslint-disable camelcase */
import React, { Component } from 'react';
import { Dialog, IconButton } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import Fade from '@material-ui/core/Fade';
import Slide from '@material-ui/core/Slide';
import { StyleSheet, css } from 'aphrodite';
import { Helper } from '../helpers/Helper';
import { CommonTheme } from '../../styles/Styles';
import { AppStyles, AppTheme } from '../../styles/AppTheme';
import { AppHelper } from '../../helpers/AppHelper';
import { DynamicForm } from './DynamicForm';
import { ApiHelper } from '../helpers/ApiHelper';

const units_to_ms = {
  milliseconds: 1,
  seconds: 1000,
  minutes: 60 * 1000,
  hours: 60 * 60 * 1000,
  days: 24 * 60 * 60 * 1000,
  weeks: 7 * 24 * 60 * 60 * 1000,
  months: 30 * 24 * 60 * 60 * 1000,
  years: 365 * 24 * 60 * 60 * 1000,
};

const unitHierarchy = ['milliseconds', 'seconds', 'minutes', 'hours', 'days', 'weeks', 'months', 'years'];

function findNextHigherUnit(currentUnit, allowedUnits) {
  if (!allowedUnits || allowedUnits.length === 0) return null;
  const currentIndex = unitHierarchy.indexOf(currentUnit);
  if (currentIndex === -1) return null;
  for (let i = currentIndex + 1; i < unitHierarchy.length; i++) {
    if (allowedUnits.includes(unitHierarchy[i])) {
      return unitHierarchy[i];
    }
  }
  return null;
}

function convertToHigherUnit(value, fromUnit, toUnit) {
  const fromMs = units_to_ms[fromUnit];
  const toMs = units_to_ms[toUnit];
  if (!fromMs || !toMs) return value;
  return Math.floor((value * fromMs) / toMs);
}

function convertValueUnitToMs(value, unit) {
  if (value === '' || value === null || value === undefined || !unit || isNaN(Number(value))) return null;
  const n = Number(value);
  return n * units_to_ms[unit];
}

function convertMsToValueUnit(ms, allowedUnits) {
  if (ms === null || ms === undefined || ms === '' || Number.isNaN(Number(ms)) || ms === 0 || ms === '0') {
    return { amount: '', unit: '', direction: 'after' };
  }
  const absMs = Math.abs(Number(ms));
  const direction = Number(ms) < 0 ? 'before' : 'after';
  const units = ['years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds', 'milliseconds'];

  for (const unit of units) {
    if (absMs % units_to_ms[unit] === 0) {
      let finalUnit = unit;
      let finalAmount = Math.floor(absMs / units_to_ms[unit]);

      if (Array.isArray(allowedUnits) && allowedUnits.length > 0 && !allowedUnits.includes(unit)) {
        const nextHigherUnit = findNextHigherUnit(unit, allowedUnits);
        if (nextHigherUnit) {
          finalUnit = nextHigherUnit;
          finalAmount = convertToHigherUnit(finalAmount, unit, nextHigherUnit);
        } else {
          finalUnit = allowedUnits[allowedUnits.length - 1];
          finalAmount = convertToHigherUnit(finalAmount, unit, finalUnit);
        }
      }

      return {
        amount: String(finalAmount),
        unit: finalUnit,
        direction,
      };
    }
  }

  let fallbackUnit = 'minutes';
  let fallbackAmount = Math.floor(absMs / units_to_ms.minutes);
  if (Array.isArray(allowedUnits) && allowedUnits.length > 0 && !allowedUnits.includes('minutes')) {
    const nextHigherUnit = findNextHigherUnit('minutes', allowedUnits);
    if (nextHigherUnit) {
      fallbackUnit = nextHigherUnit;
      fallbackAmount = convertToHigherUnit(fallbackAmount, 'minutes', nextHigherUnit);
    } else {
      fallbackUnit = allowedUnits[allowedUnits.length - 1];
      fallbackAmount = convertToHigherUnit(fallbackAmount, 'minutes', fallbackUnit);
    }
  }

  return {
    amount: String(fallbackAmount),
    unit: fallbackUnit,
    direction,
  };
}

function generateDisplayText(value, unit, subtype, direction) {
  if (value !== '' && value !== null && value !== undefined && unit) {
    const absValue = Math.abs(Number(value));
    const singular = unit.endsWith('s') ? unit.slice(0, -1) : unit;
    const plural = unit;
    const displayUnit = absValue === 1 ? singular : plural;

    if (subtype === 'absolute') {
      return `${absValue} ${displayUnit}`;
    }

    return `${direction || 'after'} ${absValue} ${displayUnit}`;
  }

  return 'Set duration';
}

function getDurationFieldDef(amount, unit, subtype, direction, directionDisabled, allowedUnits) {
  const fields = [];

  if (subtype === 'relative') {
    fields.push({
      key: 'direction',
      type: 'select',
      label: 'Direction',
      hint: 'Select direction',
      disabled: !!directionDisabled,
      v_required: {
        value: true,
        err: 'Please select a direction',
      },
      placement: 'left',
      options: [
        { key: 'before', label: 'before' },
        { key: 'after', label: 'after' },
      ],
    });
  }

  const allUnitOptions = [
    { key: 'milliseconds', label: 'Milliseconds' },
    { key: 'seconds', label: 'Seconds' },
    { key: 'minutes', label: 'Minutes' },
    { key: 'hours', label: 'Hours' },
    { key: 'days', label: 'Days' },
    { key: 'weeks', label: 'Weeks' },
    { key: 'months', label: 'Months' },
    { key: 'years', label: 'Years' },
  ];

  const filteredUnitOptions = Array.isArray(allowedUnits) && allowedUnits.length > 0
    ? allUnitOptions.filter(option => allowedUnits.includes(option.key))
    : allUnitOptions;

  fields.push(
    {
      key: 'amount',
      type: 'edit_text',
      label: 'Value',
      hint: 'Enter value',
      v_required: {
        value: true,
        err: 'Please enter a value',
      },
      placement: subtype === 'relative' ? 'right' : 'left',
    },
    {
      key: 'unit',
      type: 'select',
      label: 'Unit',
      hint: 'Select unit',
      v_required: {
        value: true,
        err: 'Please select a unit',
      },
      placement: 'left',
      options: filteredUnitOptions,
    }
  );

  const formDefinition = {
    steps: [
      {
        fields,
      },
    ],
  };

  Helper.populateFormDefinitionValues(formDefinition, { amount, unit, direction });
  return formDefinition;
}

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction='up' ref={ref} {...props} />;
});

export class ScheduleDurationPicker extends Component {
  constructor(props) {
    super(props);
    this.isMobileView = Helper.isMobileView();
    const { amount, unit, direction } = convertMsToValueUnit(props.initialMs, props.allowedUnits);
    this.state = {
      isOpen: false,
      amount,
      unit,
      direction,
    };
  }

  openDurationPicker = () => {
    if (this.props.disabled) return;
    this.apiData = {
      isScheduleDuration: true,
    };
    const subtype = this.props.subtype || 'relative';
    const effectiveDirection = this.props.directionDisabled ? 'after' : this.state.direction;
    this.formDefinition = getDurationFieldDef(this.state.amount, this.state.unit, subtype, effectiveDirection, this.props.directionDisabled, this.props.allowedUnits);
    this.formDefinition.formType = 'edit';
    this.formDefinition.ctaButtonText = 'Done';
    this.setState({
      isOpen: true,
    });
  };

  closeDurationPicker = () => {
    this.setState({
      isOpen: false,
    });
  };

  componentDidUpdate(prevProps) {
    if (prevProps.initialMs !== this.props.initialMs) {
      const { amount, unit, direction } = convertMsToValueUnit(this.props.initialMs, this.props.allowedUnits);
      this.setState({
        amount,
        unit,
        direction,
        _lastInitialMs: this.props.initialMs,
      });
    }
  }

  async customSubmit(options) {
    const { formData, caller } = options;
    if (formData.isScheduleDuration) {
      return this.customSubmitForDuration(options);
    }
    return caller.submit.call(caller, formData, true);
  }

  async customSubmitForDuration(options) {
    const { formData, caller } = options;
    delete formData.isScheduleDuration;

    const subtype = this.props.subtype || 'relative';
    const amount = formData.amount;
    const unit = formData.unit;
    const direction = formData.direction || 'after';

    let errorMsg = null;

    if (amount === '' || amount === null || amount === undefined || !unit) {
      errorMsg = 'Please enter both a value and a unit.';
    } else if (isNaN(Number(amount))) {
      errorMsg = 'Duration must be a number.';
    } else if (Number(amount) < 0) {
      errorMsg = 'Duration cannot be negative.';
    } else if (subtype === 'relative' && !direction) {
      errorMsg = 'Please select before or after.';
    }

    if (errorMsg) {
      caller.setState({
        fetchState: ApiHelper.State.ERROR,
        success: false,
        message: errorMsg,
        inProcess: false,
      });
      return;
    }

    let ms = convertValueUnitToMs(amount, unit);
    if (subtype === 'relative' && direction === 'before') {
      ms = -Math.abs(ms);
    } else {
      ms = Math.abs(ms);
    }
    this.setState({
      amount,
      unit,
      direction,
      isOpen: false,
    });

    if (this.props.onDurationChange) {
      this.props.onDurationChange(ms);
    }
  }

  render() {
    const { amount, unit,direction } = this.state;
    const subtype = this.props.subtype || 'relative';
    const effectiveDirection = this.props.directionDisabled ? 'after' : direction;
    const displayText = generateDisplayText(amount, unit,subtype,effectiveDirection);
    const modalTitle = this.props.title || 'Set Duration';

    return (
      <div>
        <div
          className={
            this.props.disabled
              ? css(Styles.displayField, Styles.displayFieldDisabled)
              : css(Styles.displayField)
          }
          onClick={!this.props.disabled && this.openDurationPicker}
        >
          {displayText}
          <IconButton size='small' className={css(Styles.arrowButton)} disabled={this.props.disabled}>
            {this.state.isOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          </IconButton>
        </div>

        <Dialog
          open={this.state.isOpen}
          aria-labelledby={modalTitle}
          TransitionComponent={window.innerWidth < 768 ? Transition : Fade}
          classes={{
            root: css(Styles.customRoot),
            paper: css(Styles.customPaper, AppHelper.isDialogScrollbarEnabled() && AppStyles.dialogScrollbar),
          }}
        >
          <div className={css([Styles.modalHeader, AppStyles.modalHeader])}>
            <div className={css(Styles.modalTitle)}>{modalTitle}</div>
            <button
              className={css(Styles.modalCloseButton)}
              onClick={this.closeDurationPicker}
              disabled={this.state.inProcess}
            >
              <svg className={css(Styles.modalCloseIcon)} viewBox='0 0 36 36'>
                <path d='M28.5 9.62L26.38 7.5 18 15.88 9.62 7.5 7.5 9.62 15.88 18 7.5 26.38l2.12 2.12L18 20.12l8.38 8.38 2.12-2.12L20.12 18z' />
              </svg>
            </button>
          </div>
          <div className={css(Styles.modalContent)}>
            <DynamicForm
              apiData={this.apiData}
              formDefinition={this.formDefinition}
              customSubmit={this.customSubmit.bind(this)}
            />
          </div>
        </Dialog>
      </div>
    );
  }
}

const Styles = StyleSheet.create({
  displayField: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    minWidth: 200,
    borderRadius: AppTheme.borderRadius,
    border: '1px solid #E5E5E5',
    overflow: 'hidden',
    boxSizing: 'border-box',
    padding: '12px 0px 12px 20px',
    color: CommonTheme.textColor,
    fontSize: 16,
    fontWeight: 'initial',
    '@media (max-width: 1024px)': {
      fontSize: 10,
      minWidth: 0,
      width: 150,
      paddingTop: 10,
      paddingBottom: 10,
    },
  },
  displayFieldDisabled: {
    backgroundColor: AppTheme.secondaryColor,
    color: '#808080',
    cursor: 'default',
  },
  arrowButton: {
    marginLeft: 'auto',
    padding: 0,
  },
  modalHeader: {
    padding: 20,
    paddingLeft: 30,
    paddingRight: 30,
    paddingBottom: 14,
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    gap: 20,
    '@media (max-width: 767px)': {
      paddingLeft: 15,
      paddingRight: 15,
    },
  },
  modalTitle: {
    color: CommonTheme.textColor,
    fontSize: 26,
    fontWeight: 600,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    flex: 1,
    '@media (max-width: 767px)': {
      fontSize: 20,
      marginRight: 43,
    },
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    paddingLeft: 30,
    paddingRight: 30,
    minWidth: 500,
    '@media (max-width: 767px)': {
      minWidth: 'unset',
      paddingLeft: 15,
      paddingRight: 15,
    },
  },
  modalCloseButton: {
    padding: 0,
    backgroundColor: 'transparent',
    display: 'flex',
    outline: 'none',
    cursor: 'pointer',
    border: 'none',
  },
  modalCloseIcon: {
    width: 18,
    height: 18,
    padding: 1,
    border: '1.5px solid ' + AppTheme.primaryColor,
    fill: AppTheme.primaryColor,
    borderRadius: 8,
  },
  customRoot: {
    zIndex: 13000,
  },
  customPaper: {
    borderRadius: AppTheme.borderRadius,
    '@media (max-width: 767px)': {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      width: '100%',
      bottom: 0,
      position: 'fixed',
      margin: 0,
      marginTop: 0,
      maxHeight: '100%',
    },
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
});